import config from '@/config'
import { createAuthenticatedFetch } from '@/helpers/api/httpClient'
import { StripeCurrencyType, SubscriptionPurchaseIntentType } from '@/helpers/api/WebApiClient'

// These mirror the backend's Nibiru.Payment.Models.Response DTOs. The generated NSwag
// client doesn't type these endpoints (PaymentController's actions aren't annotated with
// ProducesResponseType, so NSwag falls back to a raw file/blob response) - so paymentApi
// talks to them directly with fetch and these hand-written response shapes instead.
export interface CreateIntentResponse {
	clientSecret: string
	isSuccess: boolean
	message: string
	transKey: string
}

export interface GetPaymentStatusResponse {
	/** created | succeeded | failed */
	status: string
	amount: number
	currency: string
	receiptUrl?: string
	failureMessage?: string
}

export interface StripePaymentHistoryRow {
	id: number
	transactionKeyId: string
	paymentIntentId?: string
	amount: number
	currency: string
	status: string
	receiptUrl?: string
	createdOn: string
	isOneTimePayment: boolean
}

export interface PaymentMethodRow {
	id: string
	brand: string
	last4: string
	expMonth: number
	expYear: number
	isDefault: boolean
}

export interface PaymentMethodListResult {
	defaultPaymentMethodId?: string
	paymentMethods: PaymentMethodRow[]
}

export interface SetupIntentResult {
	clientSecret: string
	isSuccess: boolean
	message: string
}

const apiFetch = createAuthenticatedFetch()
const base = () => config.API_URL

async function parseJson<T>(res: Response): Promise<T> {
	if (!res.ok) {
		const err = await res.json().catch(() => ({}))
		return Promise.reject(err)
	}
	return res.json() as Promise<T>
}

const tenantPath = () => '/api/payment'

export const paymentApi = {
	getHistory: async (): Promise<StripePaymentHistoryRow[]> => {
		const res = await apiFetch(`${base()}/api/payment/history`, { method: 'GET' })
		return parseJson(res)
	},

	openInvoice: (url: string) => {
		window.open(url, '_blank', 'noopener,noreferrer')
	},

	getPaymentMethods: async (): Promise<PaymentMethodListResult> => {
		const res = await apiFetch(`${base()}${tenantPath()}/payment-methods`, { method: 'GET' })
		return parseJson(res)
	},

	createSetupIntent: async (): Promise<SetupIntentResult> => {
		const res = await apiFetch(`${base()}${tenantPath()}/setup-intent`, { method: 'POST' })
		return parseJson(res)
	},

	setDefaultPaymentMethod: async (paymentMethodId: string): Promise<void> => {
		const res = await apiFetch(`${base()}${tenantPath()}/payment-methods/default`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ paymentMethodId }),
		})
		await parseJson(res)
	},

	detachPaymentMethod: async (paymentMethodId: string): Promise<void> => {
		const res = await apiFetch(`${base()}${tenantPath()}/payment-methods/detach`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ paymentMethodId }),
		})
		await parseJson(res)
	},

	createIntent: async (request: {
		subscriptionId: number
		currency?: StripeCurrencyType
		quoteId?: string
		intentType?: SubscriptionPurchaseIntentType
		previousTenantSubscriptionPlanId?: number
		subscriptionPackId?: number
		paymentMethodId?: string
	}): Promise<CreateIntentResponse> => {
		const payload = {
			currency: request.currency ?? StripeCurrencyType.USD,
			subscriptionId: request.subscriptionId,
			quoteId: request.quoteId,
			intentType: request.intentType,
			previousTenantSubscriptionPlanId: request.previousTenantSubscriptionPlanId,
			subscriptionPackId: request.subscriptionPackId,
			paymentMethodId: request.paymentMethodId,
		}

		const res = await apiFetch(`${base()}/api/payment/create-intent`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
		})
		return parseJson(res)
	},

	getPaymentStatus: async (transKey: string): Promise<GetPaymentStatusResponse> => {
		const res = await apiFetch(`${base()}/api/payment/status/${encodeURIComponent(transKey)}`, { method: 'GET' })
		return parseJson(res)
	},
}

export const formatCardBrand = (brand: string) => {
	const normalized = brand?.toLowerCase() ?? 'card'
	return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

export const formatCardExpiry = (expMonth: number, expYear: number) =>
	`${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`
